import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AzButtonComponent } from '../az-button/az-button.component';
import { AzSelectorComponent } from '../az-selector/az-selector.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ScrollActivityDirective } from '../../directives/scroll-activity.directive';
import {
  LegalEntityContact,
  RecipientGroup,
} from '../../../features/document-repository/models/document-entry.model';

@Component({
  selector: 'app-az-document-update-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AzButtonComponent,
    AzSelectorComponent,
    SpinnerComponent,
    ScrollActivityDirective,
  ],
  templateUrl: './az-document-update-modal.component.html',
  styleUrl: './az-document-update-modal.component.scss',
})
export class AzDocumentUpdateModalComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Input() isUploading: boolean = false;
  @Input() documentTypes: string[] = [];
  @Input() allowedFileTypes: string[] = []; // e.g., ['xlsx', 'docx']
  @Input() showCommentBox: boolean = true; // Control visibility of comment box
  @Input() preselectedDocumentType: string = ''; // Pre-selected document type
  @Input() disableDocumentTypeSelector: boolean = false; // Disable the selector
  @Input() showNotificationStep: boolean = false; // Enable two-step notification flow
  @Input() contactsValidationWarning: string = ''; // Used for Confirm Contract disabled gate
  @Input() incompleteContacts: LegalEntityContact[] = []; // Structured list for banner + detail panel
  @Input() isValidatingContacts: boolean = false; // True while contacts API call is in-flight
  @Output() close = new EventEmitter<void>();
  @Output() upload = new EventEmitter<{
    file: File | null;
    documentType: string;
    comments: string;
  }>();
  @Output() stepOneConfirmed = new EventEmitter<{
    file: File | null;
    documentType: string;
    comments: string;
  }>();
  @Output() recipientGroupChanged = new EventEmitter<RecipientGroup>();
  @Output() notifyConfirmed = new EventEmitter<{
    replacementFile: File;
    documentType: string;
    adobePdf: File;
    recipientGroup: RecipientGroup;
  }>();

  // Step 1 state
  selectedFile: File | null = null;
  selectedDocumentType: string = '';
  comments: string = '';
  isDragging: boolean = false;
  errorMessage: string = '';

  // Step 2 / 3 state
  readonly currentStep = signal<1 | 2 | 3>(1);
  readonly step1Data = signal<{ file: File | null; documentType: string; comments: string } | null>(
    null,
  );
  readonly adobePdfFile = signal<File | null>(null);
  readonly adobePdfError = signal<string>('');
  readonly selectedRecipientGroup = signal<RecipientGroup | null>(null);

  readonly showAffectedEntities = signal(false);

  readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

  ngOnChanges(changes: SimpleChanges): void {
    // Pre-select document type when modal opens or preselectedDocumentType changes
    if (changes['preselectedDocumentType'] && this.preselectedDocumentType) {
      this.selectedDocumentType = this.preselectedDocumentType;
    }

    // Re-apply preselection when modal opens
    if (changes['isOpen'] && this.isOpen && this.preselectedDocumentType) {
      this.selectedDocumentType = this.preselectedDocumentType;
    }

    // Auto-select when only one document type is available, if no preselection is provided
    if (
      changes['documentTypes'] &&
      !this.preselectedDocumentType &&
      this.documentTypes.length === 1
    ) {
      this.selectedDocumentType = this.documentTypes[0];
    }

    // Auto-select when modal opens with single document type, if no preselection is provided
    if (
      changes['isOpen'] &&
      this.isOpen &&
      !this.preselectedDocumentType &&
      this.documentTypes.length === 1
    ) {
      this.selectedDocumentType = this.documentTypes[0];
    }

    // Reset all state (including step 2) when modal is closed
    if (changes['isOpen'] && !this.isOpen && !changes['isOpen'].firstChange) {
      this.resetForm();
    }
  }

  get ALLOWED_EXTENSIONS(): string[] {
    if (this.allowedFileTypes.length > 0) {
      return this.allowedFileTypes.map((type) => `.${type.toLowerCase()}`);
    }
    return ['.docx', '.xlsx']; // Default fallback
  }

  get acceptedFormats(): string {
    return this.ALLOWED_EXTENSIONS.join(',');
  }

  get supportedFormatsText(): string {
    return this.ALLOWED_EXTENSIONS.join(', ');
  }

  get shouldShowDocumentTypeSelector(): boolean {
    return this.documentTypes.length > 1;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.validateAndSetFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.validateAndSetFile(event.dataTransfer.files[0]);
    }
  }

  validateAndSetFile(file: File): void {
    this.errorMessage = '';

    // Validate file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = this.ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));

    if (!hasValidExtension) {
      this.errorMessage = `Invalid file format. Please upload ${this.supportedFormatsText} files only.`;
      this.selectedFile = null;
      return;
    }

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      this.errorMessage = 'File size exceeds 10MB. Please upload a smaller file.';
      this.selectedFile = null;
      return;
    }

    // File is valid
    this.selectedFile = file;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.errorMessage = '';
  }

  get selectedFileName(): string {
    return this.selectedFile ? this.selectedFile.name : '';
  }

  get selectedFileSize(): string {
    if (!this.selectedFile) return '';
    const sizeInMB = (this.selectedFile.size / (1024 * 1024)).toFixed(2);
    return `${sizeInMB} MB`;
  }

  onCancel(): void {
    this.resetForm();
    this.close.emit();
  }

  onConfirm(): void {
    if (this.showNotificationStep) {
      // Two-step mode: store step 1 data and advance to step 2
      const data = {
        file: this.selectedFile,
        documentType: this.selectedDocumentType,
        comments: this.comments,
      };
      this.step1Data.set(data);
      this.currentStep.set(2);
      this.stepOneConfirmed.emit(data);
    } else {
      // Standard mode: emit upload immediately
      this.upload.emit({
        file: this.selectedFile,
        documentType: this.selectedDocumentType,
        comments: this.comments,
      });
    }
  }

  onDocumentTypeChange(type: string): void {
    this.selectedDocumentType = type;
  }

  // Step 2: Adobe PDF handling
  validateAndSetAdobePdf(file: File): void {
    this.adobePdfError.set('');
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.pdf')) {
      this.adobePdfError.set('Invalid file format. Please upload .pdf files only.');
      this.adobePdfFile.set(null);
      return;
    }
    if (file.size > this.MAX_FILE_SIZE) {
      this.adobePdfError.set('File size exceeds 10MB. Please upload a smaller file.');
      this.adobePdfFile.set(null);
      return;
    }
    this.adobePdfFile.set(file);
  }

  removeAdobePdf(): void {
    this.adobePdfFile.set(null);
    this.adobePdfError.set('');
  }

  onAdobePdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.validateAndSetAdobePdf(input.files[0]);
    }
  }

  onAdobePdfDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onAdobePdfDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onAdobePdfDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.validateAndSetAdobePdf(event.dataTransfer.files[0]);
    }
  }

  get adobePdfFileSize(): string {
    const file = this.adobePdfFile();
    if (!file) return '';
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    return `${sizeInMB} MB`;
  }

  // Step 2: Navigation
  onBack(): void {
    this.currentStep.set(1);
  }

  // Step 3: Back to step 2
  onStep3Back(): void {
    this.currentStep.set(2);
  }

  // Step 2: Recipient selection
  onRecipientSelect(group: RecipientGroup): void {
    this.selectedRecipientGroup.set(group);
    this.recipientGroupChanged.emit(group);
  }

  get confirmContractDisabled(): boolean {
    return (
      !this.adobePdfFile() ||
      !!this.adobePdfError() ||
      !this.selectedRecipientGroup() ||
      !!this.contactsValidationWarning ||
      this.isValidatingContacts
    );
  }

  // Step 2: Confirm → advance to step 3 (review screen)
  onNotifyConfirm(): void {
    if (this.confirmContractDisabled) return;
    const s1 = this.step1Data();
    const adobePdf = this.adobePdfFile();
    const group = this.selectedRecipientGroup();
    if (!s1?.file || !adobePdf || !group) return;
    this.currentStep.set(3);
  }

  // Step 3: Final confirm — emit the upload event
  onStep3Confirm(): void {
    const s1 = this.step1Data();
    const adobePdf = this.adobePdfFile();
    const group = this.selectedRecipientGroup();
    if (!s1?.file || !adobePdf || !group) return;
    this.notifyConfirmed.emit({
      replacementFile: s1.file,
      documentType: s1.documentType,
      adobePdf,
      recipientGroup: group,
    });
  }

  get recipientGroupLabel(): string {
    if (this.selectedRecipientGroup() === 'iga_coordinators_and_contacts') {
      return 'IGA Coordinators and document contact list';
    }
    return 'IGA Coordinators';
  }

  private static readonly NUMBER_WORDS = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
  ];

  get incompleteContactsCountLabel(): string {
    const n = this.incompleteContacts.length;
    if (n >= 1 && n <= 10) {
      return AzDocumentUpdateModalComponent.NUMBER_WORDS[n];
    }
    return String(n);
  }

  downloadFile(file: File | null | undefined): void {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  }

  showAffectedEntitiesPanel(): void {
    this.showAffectedEntities.set(true);
  }

  hideAffectedEntitiesPanel(): void {
    this.showAffectedEntities.set(false);
  }

  resetForm(): void {
    this.selectedFile = null;
    this.selectedDocumentType = '';
    this.comments = '';
    this.isDragging = false;
    this.errorMessage = '';
    // Reset step 2 / 3 state
    this.currentStep.set(1);
    this.step1Data.set(null);
    this.adobePdfFile.set(null);
    this.adobePdfError.set('');
    this.selectedRecipientGroup.set(null);
    this.showAffectedEntities.set(false);
  }
}
